import { LanguageConstants } from './LanguageConstants';
import { getRuntimeEnv } from '../config/runtimeConfig';

// Get runtime environment variables (with fallback to import.meta.env)
const env = getRuntimeEnv();

// API Configuration
export const API_CONFIG = {
  API_BASE_URL: `${env.VITE_API_BASE_URL}/api/v1`,
  EMAIL_API_BASE_URL: `${env.VITE_API_BASE_URL}/email/`,
  ML_API_BASE_URL: env.VITE_API_BASE_URL,
  NODE_BASE_URL: env.VITE_NODE_BASE_URL || 'http://localhost:3001',
  SOCKET_URL: env.VITE_SOCKET_URL,
  // Prism7 ACL Access Control / Trusted Advisor dashboards (temporary, channel-gated feature)
  SHAY_DASHBOARD_BASE_URL: `${env.VITE_API_BASE_URL}/shay/dashboard`,
  TIMEOUT: 10000, // 10 seconds
};

/** Build auth headers for the Node OAuth proxy from the current Shay user session. */
export const getNodeApiHeaders = (): Record<string, string> => {
  if (typeof window === 'undefined') {
    return {};
  }
  const token = localStorage.getItem(STORAGE_KEYS.AUTH_TOKEN);
  return token ? { Authorization: `Bearer ${token}` } : {};
};

// Socket Configuration
export const SOCKET_CONFIG = {
  // Local development
  LOCAL: {
    SOCKET_URL: env.VITE_SOCKET_URL || 'ws://localhost:3001'
  },
  // Production - use base URL, path will be set in socketService
  PRODUCTION: {
    // In production (AWS App Runner), use the same domain as the app
    // If VITE_SOCKET_URL is set, use it; otherwise derive from current location
    SOCKET_URL: env.VITE_SOCKET_URL || (typeof window !== 'undefined' 
      ? `${window.location.protocol === 'https:' ? 'wss:' : 'ws:'}//${window.location.host}`
      : 'wss://localhost:3001')
  }
};

// Helper function to get socket URL based on environment
// Returns base URL without /socket.io/ path (path is set in socketService)
export const getSocketUrl = (): string => {
  const isProduction = window.location.hostname !== 'localhost' && 
                      window.location.hostname !== '127.0.0.1';
  
  if (isProduction) {
    // In production (AWS App Runner), use same origin as the page
    // This ensures Socket.IO works on the same domain/port as the React app
    // If VITE_SOCKET_URL is explicitly set and different, use it
    const configuredUrl = SOCKET_CONFIG.PRODUCTION.SOCKET_URL;
    const currentOrigin = `${window.location.protocol === 'https:' ? 'wss:' : 'ws:'}//${window.location.host}`;
    
    // If VITE_SOCKET_URL is set and it's a different origin, use it
    // Otherwise, use the current page's origin (same origin as React app)
    if (configuredUrl && configuredUrl !== currentOrigin) {
      // Remove /socket.io/ if it was accidentally included in the env var
      return configuredUrl.replace(/\/socket\.io\/?$/, '');
    }
    
    // Default to same origin as the page (works for AWS App Runner single-port setup)
    return currentOrigin;
  } else {
    // Local development - use explicit URL or default
    return SOCKET_CONFIG.LOCAL.SOCKET_URL || 'ws://localhost:3001';
  }
};

// API Endpoints
export const API_ENDPOINTS = {
  // Auth endpoints
  AUTH: {
    LOGIN: '/user-auth/login',
    LOGOUT: '/auth/logout',
    REFRESH: '/auth/refresh',
  },
  // User management endpoints
  USERS: {
    LIST_COMPANY_USERS: (companyId: string) => `/users/company/${companyId}/users`,
    USER_BY_ID: (id: string) => `/users/${id}`,
    CREATE_USER: '/users/',
    UPDATE_USER: (userId: string, companyId: string) => `/users/company/${companyId}/users/${userId}`,
    DELETE_USER: (userId: string, companyId: string) => `/users/company/${companyId}/users/${userId}`,
    GET_COMPANY_INVITATIONS: (companyId: string) => `/companies/${companyId}/invitations`,
    DELETE_PENDING_INVITATIONS: (companyId: string, email: string) => `/users/company/${companyId}/pendingInvitedUsers/${email}`,
    GET_COMPANY_ACCESS_REQUESTS: (companyId: string) => `/companies/${companyId}/access-requests`,
    APPROVE_ACCESS_REQUEST: (companyId: string, requestId: string) => `/companies/${companyId}/access-requests/${requestId}/approve`,
    REJECT_ACCESS_REQUEST: (companyId: string, requestId: string) => `/companies/${companyId}/access-requests/${requestId}/reject`,
  },
  // Channel management endpoints
  CHANNELS: {
    LIST: '/channels/',
    /** Message channels for a workspace (GET query: workspace_id, optional search, is_archived) – used e.g. for Move Thread */
    MESSAGE_CHANNELS: '/channels/message-channels',
    CREATE: '/channels/',
    GET_BY_ID: (channelId: string) => `/channels/${channelId}`,
    UPDATE: (channelId: string) => `/channels/${channelId}`,
    DELETE: (channelId: string) => `/channels/${channelId}`,
    TAGS: (page: number = 1, size: number = 15) => `/channels/tags?page=${page}&size=${size}`,
    LIST_MEMBERS: (channelId: string) => `/channels/${channelId}/members`,
    MEMBER_BY_ID: (channelId: string, memberId: string) => `/channels/${channelId}/members/${memberId}`,
    ADD_MEMBER: (channelId: string) => `/channels/${channelId}/members`,
    ADD_MEMBERS_BULK: (channelId: string) => `/channels/${channelId}/members/bulk`,
    UPDATE_MEMBER: (channelId: string, memberId: string) => `/channels/${channelId}/members/${memberId}`,
    REMOVE_MEMBER: (channelId: string, memberId: string) => `/channels/${channelId}/members/${memberId}`,
    SEARCH_MEMBERS: (companyId: string, searchTerm: string) => `/users/company/${companyId}/users?page=1&size=10&search=${encodeURIComponent(searchTerm)}&sort_by=name&sort_order=asc`,
    WORKSPACES: (page: number = 1, size: number = 10, search?: string, sortBy: string = 'name', sortOrder: 'asc' | 'desc' = 'asc') => `/channels/workspaces?page=${page}&size=${size}${search ? `&search=${encodeURIComponent(search)}` : ''}&sort_by=${sortBy}&sort_order=${sortOrder}`,
    UPDATE_WORKSPACE: (workspaceId: string) => `/workspaces/${workspaceId}`,
    DELETE_WORKSPACE: (workspaceId: string) => `/workspaces/${workspaceId}`,
  },
  // Admin endpoints
  ADMIN: {
    USERS: '/admin/users',
    USER_BY_ID: (id: string) => `/admin/users/${id}`,
    CREATE_USER: '/admin/users',
    UPDATE_USER: (id: string) => `/admin/users/${id}`,
    DELETE_USER: (id: string) => `/admin/users/${id}`,
    SUBSCRIPTION: '/admin/subscription',
    SUBSCRIPTION_BY_ID: (id: string) => `/admin/subscription/${id}`,
    UPDATE_SUBSCRIPTION: (id: string) => `/admin/subscription/${id}`,
  },
  // Support endpoints
  SUPPORT: {
    TICKETS: '/support/tickets',
    TICKET_BY_ID: (id: string) => `/support/tickets/${id}`,
    CREATE_TICKET: '/support/tickets',
    UPDATE_TICKET: (id: string) => `/support/tickets/${id}`,
    CLOSE_TICKET: (id: string) => `/support/tickets/${id}/close`,
  },
  // Messages and Threads endpoints
  MESSAGES: {
    LIST_THREADS: '/messages/threads',
    GET_MESSAGES: (threadId: string) => `/messages/${threadId}`,
    DELETE_MESSAGE: '/messages/delete',
    /** Hide sent email (e.g. from Sent folder) – POST messages/message/{message_id}/hide */
    HIDE_MESSAGE: (messageId: string) => `/messages/message/${messageId}/hide`,
    GET_THREAD_DETAILS: (threadId: string) => `/messages/threads/${threadId}`,
    GET_MESSAGE_DETAILS: (messageId: string) => `/messages/message/${messageId}`,
    CREATE_MESSAGE: '/messages/',
    UPDATE_MESSAGE: '/messages/update',
    CREATE_THREAD: '/messages/threads',
    UPDATE_THREAD: (threadId: string) => `/messages/threads/${threadId}`,
    /** POST body: { thread_ids: string[] } */
    ARCHIVE_THREADS_BULK: '/messages/threads/archive/bulk',
    /** POST body: { thread_ids: string[] } — sets is_archived=false */
    UNARCHIVE_THREADS_BULK: '/messages/threads/unarchive/bulk',
    /** POST soft-delete: { thread_ids: string[] } — sets is_active=false */
    DELETE_THREADS_BULK: '/messages/threads/delete/bulk',
    DELETE_THREAD: (threadId: string) => `/messages/threads/${threadId}`,
    /** Move thread to another channel – POST; body: { thread_id, new_channel_id } */
    MOVE_THREAD: '/messages/threads/move',
  },
  // Attachments endpoints (message/regular channels use main API; email uses email API)
  ATTACHMENTS: {
    UPLOAD: '/attachments/upload',
    GET_DETAILS: (attachmentId: string) => `/attachments/${attachmentId}`,
  },
  // Datasources endpoints
  DATASOURCES: {
    LIST: '/datasources/',
    DELETE: (datasourceId: string) => `/datasources/${datasourceId}`,
    DOWNLOAD: (datasourceId: string) => `/datasources/${datasourceId}/download`,
    BULK_CONNECTION: '/datasources/bulk-connection',
    UPLOAD_BULK: '/datasources/upload/bulk',
    CREATE_BULK: '/datasources/bulk',
    SUPPORTED_LOG_TYPES: '/datasources/supported-log-types',
    EMBEDDING_STATUS: '/datasources/embedding-status/',
    REGENERATE_EMBEDDING: (datasourceId: string) => `/datasources/embedding/regenerate/${datasourceId}`,
    REFRESH: (datasourceId: string) => `/datasources/${datasourceId}/refresh`,
  },
  SHAREPOINT: {
    FILES: '/datasources/sharepoint/files',
  },
  // Apps endpoints
  APPS: {
    LIST: '/apps/',
    CATEGORIES: '/apps/categories',
    GET_BY_ID: (appId: string) => `/apps/${appId}`,
  },
  // App Accounts endpoints
  APP_ACCOUNTS: {
    LIST: '/app-accounts/',
    DELETE: (connectionId: string) => `/app-accounts/${connectionId}`,
    // Best-effort Odoo database discovery for the connect dialog (db.list()).
    ODOO_DATABASES: '/app-accounts/odoo/databases',
  },
  // User Authentication endpoints
  USER_AUTH: {
    REGISTER: '/user-auth/register',
    /** PUT – update current user personal info (name, avatar_url, preferences) */
    PROFILE: '/user-auth/profile',
    PASSWORD_CHANGE: '/user-auth/password',
    BULK_INVITE: '/user-auth/bulk-invite',
    FORGOT_PASSWORD: '/user-auth/forgot-password',
    RESET_PASSWORD: '/user-auth/reset-password',
    SEND_VERIFICATION_EMAIL: '/user-auth/send-verification-email',
    VERIFY_EMAIL: '/public/verify-email',
    RESEND_VERIFICATION: '/public/resend-verification',
  },
  /** Invite / org SSO — GET initiate returns { authorization_url, state }; callback is server-side */
  SSO: {
    INITIATE: (provider: 'google' | 'microsoft') => `/sso/${provider}/initiate`,
  },
  // Public web SSO access-request endpoints. These must not use the Gmail add-on API key.
  WEB_ACCESS: {
    ACCESS_CHECK: '/public/web/access-check',
    ACCESS_REQUESTS: '/public/web/access-requests',
  },
  // Company and Onboarding endpoints
  COMPANY: {
    SIGNUP: '/public/companies',
    FETCH_PLANS: '/public/fetchPlans',
    MY_SUBSCRIPTION: '/public/mySubscription',
    GET_BY_ID: (companyId: string) => `/companies/${companyId}`,
    GET_MY_COMPANY: '/companies/my',
  },
  // Chat service / LLM token endpoints
  CHAT_SERVICE: {
    SAVE_TOKEN_DETAILS: '/public/saveTokenDetails',
    VALIDATE_TOKEN: '/llmValidation/gpt-token-validation',
    FETCH_TOKEN_DETAILS: '/fetchTokenDetails',
    UPDATE_TOKEN_DETAILS: '/updateTokenDetails',
    DELETE_TOKEN_DETAILS: '/deleteTokenDetails',
  },
  // Subscription Plans endpoints
  SUBSCRIPTION_PLANS: {
    ACTIVE_PLANS: '/subscriptions/company/my/current-plan',
  },
  // Payment Gateway endpoints
  PAYMENT: {
    ZOHO: {
      CREATE: '/payment-gateway/zoho/subscriptions/create',
      CREATE_HOSTED_PAGE: '/payment-gateway/zoho/subscriptions/hostedPage/create',
      HOSTED_PAGE_UPDATE: '/payment-gateway/zoho/subscriptions/hostedPage/update',
      SUBSCRIPTION_STATUS: '/payment-gateway/zoho/subscriptions/status',
      CANCEL_SUBSCRIPTION: '/payment-gateway/zoho/subscriptions/cancel',
      /** POST body: { subscriptionId, billingCycles, platform_name } */
      REACTIVATE_SUBSCRIPTION: '/payment-gateway/zoho/subscriptions/reactivate',
      FETCH_BY_HOSTED_ID: '/payment-gateway/zoho/subscriptions/fetchByHostedId',
      VALIDATE_PAYMENT: '/payment-gateway/zoho/subscriptions/validatePayment',
      UPDATE_COMPANY_PLAN: '/payment-gateway/zoho/subscriptions/updateCompanyPlan',
      VALIDATE_SUBSCRIPTION: (userId: string) => `/payment-gateway/zoho/subscriptions/validation/${userId}`,
      FETCH_COMPANY_SUBSCRIPTION_DETAILS: '/payment-gateway/zoho/subscriptions/fetchCompanySubscriptionDetails',
      INVOICE_DOWNLOAD: (invoiceId: string) => `/payment-gateway/zoho/invoices/${invoiceId}/download`,
      /** Validate subscription for channel creation / message posting; optional body: { channelId } */
      SUBSCRIPTION_VALIDATION: (userId: string) => `/payment-gateway/zoho/subscriptions/validation/${userId}`,
    },
  },
  // Dashboard endpoints
  DASHBOARD: {
    STATS: '/dashboard/stats',
    RECENT_ACTIVITY: '/dashboard/recent-activity',
    LOG_ANALYSIS: '/dashboard/log-analysis',
  },
  // Notifications endpoints
  NOTIFICATIONS: {
    REAL_TIME_SETTINGS: (userId: string, channelId: string) => `/notifications/realTimeNotificationSettings/${userId}/${channelId}`,
    EMAIL_NOTIFY_SETTINGS: (userId: string) => `/notifications/emailnotifysettings/${userId}`,
    EMAIL_NOTIFY_SAVE: '/notifications/emailnotifysettings/save',
    EMAIL_NOTIFY_UPDATE: '/notifications/emailnotifysettings/update',
    SAVE: '/notifications/save',
    UPDATE: '/notifications/update',
  },
  // Skills endpoints
  SKILLS: {
    LIST: (channelId: string) => `/skills/?channel_id=${encodeURIComponent(channelId)}`,
    /** Trailing slash avoids 307 redirect (extra preflight + CORS failure on redirect). */
    CREATE: '/skills/',
    UPDATE: (skillId: string) => `/skills/${skillId}`,
    PATCH: (skillId: string) => `/skills/${skillId}`,
    TOGGLE: (skillId: string) => `/skills/${skillId}/toggle`,
    DELETE: (skillId: string) => `/skills/${skillId}`,
    VALIDATE: '/skills/validate',
    DISCOVER_SCHEMA: '/skills/discover-schema',
  },
  // Approvals endpoints
  APPROVALS: {
    LIST: '/approvals/',
    GET_BY_ID: (id: string) => `/approvals/${id}`,
    CREATE: '/approvals/',
    UPDATE: (id: string) => `/approvals/${id}`,
    DELETE: (id: string) => `/approvals/${id}`,
    APPROVE: (id: string) => `/approvals/${id}/approve`,
    REJECT: (id: string) => `/approvals/${id}/reject`,
    ACTION: (id: string) => `/approvals/${id}/action`,
    REASSIGN: (id: string) => `/approvals/${id}/reassign`,
    COMMENT: (id: string) => `/approvals/${id}/comment`,
    COUNT: '/approvals/count',
  },
  // Tasks endpoints
  TASKS: {
    LIST: '/tasks/',
    GET_BY_ID: (id: string) => `/tasks/${id}`,
    CREATE: '/tasks/',
    UPDATE: (id: string) => `/tasks/${id}`,
    DELETE: (id: string) => `/tasks/${id}`,
    UPDATE_STATUS: (id: string) => `/tasks/${id}/status`,
    COUNT: '/tasks/count',
  },
  // Checklists endpoints
  CHECKLISTS: {
    COMPLETE_ITEM: (itemId: string) => `/checklists/checklist/${itemId}/complete`,
    UPDATE_ITEM: (itemId: string) => `/checklists/checklist/${itemId}`,
    DELETE_ITEM: (itemId: string) => `/checklists/checklist/${itemId}`,
    ADD_ITEM: (taskId: string) => `/checklists/task/${taskId}/checklists/add`,
    REORDER_ITEMS: (taskId: string) => `/checklists/task/${taskId}/checklists/reorder`,
  },
};

//ML API Endpoints
export const EMAIL_API_ENDPOINTS = {
  CONNECT: 'connect',
  TEST_CONNECTION: 'test-connection',
  LIST_THREADS: (channelId: string) =>  `email/channels/${channelId}/threads`,
  GET_MESSAGES: (threadId: string, messageId?: string) => `email/threads/${threadId}/messages${messageId ? `?messageId=${messageId}` : ''}`,
  GET_EMAIL_METRICS: (channelId: string) => `email/channels/${channelId}/metrics`,
  COMPOSE_SEND: 'compose/send_compose_email',
  CREATE_SIGNATURE_SALUTATION: 'signature-salutation/',
  GET_SIGNATURE_SALUTATIONS: 'signature-salutation/',
  UPDATE_SIGNATURE_SALUTATION: (signatureSalutationId: string) => `signature-salutation/${signatureSalutationId}`,
  DELETE_SIGNATURE_SALUTATION: (signatureSalutationId: string) => `signature-salutation/${signatureSalutationId}`,
  GET_EMAIL_SYNC_STATUS: (emailAddress: string) => `email/status/${encodeURIComponent(emailAddress)}`,
  DISCONNECT_EMAIL: (emailAddress: string, disconnectOption: string = 'stop_sync') => `email/disconnect?email_address=${encodeURIComponent(emailAddress)}&disconnect_option=${disconnectOption}`,
  RECONNECT_EMAIL: (emailAddress: string) => `email/reconnect/${encodeURIComponent(emailAddress)}`,
  REAUTHORIZE_EMAIL: (emailAddress: string) => `email/reconnect/${encodeURIComponent(emailAddress)}/reauthorize`,
  DELETE_EMAIL_DATA: (emailAddress: string, archiveOnly: boolean = false) => `email/cleanup/${encodeURIComponent(emailAddress)}?archive_only=${archiveOnly}`,
  MOVE_TO_INBOX: 'email/move-to/inbox',
  /** Move thread to target classification (inbox, spam, no_reply) – whole thread */
  MOVE_TO_CLASSIFICATION: (classification: string) => `email/move-to/${classification}`,
  UPLOAD_ATTACHMENTS: 'attachments',
  GET_ATTACHMENT_DETAILS: (attachmentId: string, includePresignedUrl?: boolean) => 
    `attachments/${attachmentId}${includePresignedUrl ? '?include_presigned_url=true' : ''}`,
  HIDE_DRAFT: (draftId: string) => `drafts/${draftId}/hide`,
  ADD_ATTACHMENTS_TO_DRAFT: (draftId: string) => `drafts/${draftId}/attachments:add`,
  REMOVE_ATTACHMENTS_FROM_DRAFT: (draftId: string) => `drafts/${draftId}/attachments:remove`,
  EMAIL_IDS_SEARCH: 'emailIds/search',
    // Dashboard Endpoints
  GET_THROUGHPUT_METRICS: 'dashboard/metrics/throughput',
  GET_STRATEGIC_INSIGHTS: 'dashboard/insights/strategic-insights',
  GET_BUSINESS_OUTCOME_METRICS: 'dashboard/metrics/business-outcomes',
  GET_OVERVIEW_METRICS: 'dashboard/overview',
  GET_TASKS_AND_APPROVALS_METRICS: 'dashboard/metrics/tasks-approvals',
  GET_TIMELINE_METRICS: 'dashboard/metrics/timeline',
  GET_APP_USAGE_METRICS: 'dashboard/metrics/app-usage',
  GET_FLOW_RUN_METRICS: (channelId: string) => `dashboard/${encodeURIComponent(channelId)}/flow-runs`,
};

// Messaging API Endpoints
export const MESSAGING_API_ENDPOINTS = {
  CONNECT: 'messaging/connect',
  SEND_MESSAGE: 'messaging/send_message',
  LIST_CONNECTIONS: 'messaging/connections',
  GET_CONNECTION_STATUS: (appAccountId: string) => `messaging/connections/${appAccountId}/status`,
  MANUAL_SYNC: (appAccountId: string) => `messaging/connections/${appAccountId}/sync`,
  DELETE_CONNECTION: (appAccountId: string) => `messaging/connections/${appAccountId}`,
  TEST_CONNECTION: 'messaging/test-connection',
  CREATE_WHATSAPP_THREAD: 'messaging/threads/whatsapp',
  // Google Chat Inbox (DM-only, OAuth2 real-mailbox provider) — separate endpoint/schema from
  // CONNECT's google_chat (named-space, service-account) provider. See docs/GoogleChatInbox_DM_FE_Implementation.md.
  CONNECT_GOOGLE_CHAT_INBOX: 'messaging/google-chat-inbox/connect',
};

//ML API Endpoints
export const ML_API_ENDPOINTS = {
  // Agents endpoints
  AGENTS: {
    GET_ALL: '/rest/agents',
    GET_AGENTS: '/shay/email/getAgents',
  },
  // AI Chat endpoints
  AI_CHAT: {
    MAIN_CHAT: '/shay/email/main_chat',
  },
  // Database endpoints
  DATABASE: {
    TEST_CONNECTION: '/shay/email/testDatabaseConnection',
    GET_ALL_TABLES: '/shay/email/getAllTables',
    GET_ROWS: '/shay/email/getRows',
  },
  // Email description generation
  GENERATE_DESCRIPTION: '/shay/email/generate-description',
  // Email keywords generation
  GENERATE_KEYWORDS: '/shay/email/generate-keywords',
  // Business rules generation
  GENERATE_BUSINESS_RULES: '/shay/email/generate-business-rules',
  // Channel business rules validation
  VALIDATE_CHANNEL_RULES: '/shay/email/validate-channel-rules',
  // Topic/classification training (retrain)
  TOPIC_TRAIN_WRAPPER: '/shay/email/topic/train-wrapper',
  // stdio MCP skill connection test — spins up the subprocess and lists its tools
  VALIDATE_SKILL_CONNECTION: '/api/v1/skills/validate',
};

// Workflow Canvas — graph-based business rule builder (Temporal-backed). Base: ML_API_BASE_URL (no /api/v1 prefix).
export const WORKFLOW_CONFIG_API_ENDPOINTS = {
  /** NEW — one row per distinct saved workflow name (latest version's summary), company-wide. Ref: Workflow_Save_API_Integration.md §6. */
  NAMES: '/shay/email/workflow-config/names',
  GET_CONFIG: (name: string) => `/shay/email/workflow-config/${encodeURIComponent(name)}`,
  GET_VERSIONS: (name: string) => `/shay/email/workflow-config/${encodeURIComponent(name)}/versions`,
  /** NEW — fetches ONE specific past version's actual graph content (ref: Workflow Canvas — Version History "Load" Fix, 2026-07-24). */
  GET_VERSION: (name: string, version: number) => `/shay/email/workflow-config/${encodeURIComponent(name)}/versions/${version}`,
  VALIDATE: (name: string) => `/shay/email/workflow-config/${encodeURIComponent(name)}/validate`,
  SAVE: (name: string) => `/shay/email/workflow-config/${encodeURIComponent(name)}`,
  DRAFT: (name: string) => `/shay/email/workflow-config/${encodeURIComponent(name)}/draft`,
  RUN_TEST: (name: string) => `/shay/email/workflow-config/${encodeURIComponent(name)}/run-test`,
  RUN_TEST_CURRENT_NODE: (workflowId: string) => `/shay/email/workflow-config/run-test/${encodeURIComponent(workflowId)}/current-node`,
  RUN_TEST_STATUS: (workflowId: string) => `/shay/email/workflow-config/run-test/${encodeURIComponent(workflowId)}/status`,
  RUN_TEST_SIGNAL: (workflowId: string) => `/shay/email/workflow-config/run-test/${encodeURIComponent(workflowId)}/signal`,
};

// Action Catalog — friendly business actions that compile to Workflow Canvas primitives server-side.
// Ref: 06_Frontend_API_Changes_Action_Catalog_2026-07-20.md §2. Read-only; no write/admin endpoints yet.
export const WORKFLOW_CATALOG_API_ENDPOINTS = {
  ACTIONS: '/shay/email/workflow-config/catalog/actions',
  CONNECTIONS: '/shay/email/workflow-config/catalog/connections',
};

// Channel Rule Flows — parses a channel's business-rules text into editable TRIGGER/ACTION/
// CONDITION flow diagrams. Ref: PRISM7_CHANNEL_RULES_CANVAS_TDD.md §1/§4/§5 — confirmed contract
// (this session's TDD, superseding the earlier provisional single-owner guess): list/get/confirm
// are owned and persisted by BE (base path `channel-rule-flows/...`, EMAIL_API_BASE_URL, which
// already ends in a trailing slash — no leading slash here, matching EMAIL_API_ENDPOINTS'
// convention). parse/serialize are compute-only ORCH routes (base path
// `/shay/email/channel-rules/...`, ML_API_BASE_URL, leading slash — matching
// WORKFLOW_CONFIG_API_ENDPOINTS' convention). ORCH never writes gg_channel_rule_flows directly —
// its own parse step calls BE's internal /channel-rule-flows/{id}/replace server-to-service,
// which is not FE-reachable and has no constant here.
export const CHANNEL_RULE_FLOWS_API_ENDPOINTS = {
  /** GET (BE) — every flow for this channel, full nodes/edges inline (TDD §4.2 amendment — no
   * separate per-flow round trip needed for the common "open tab, first flow auto-renders" case). */
  LIST: (channelId: string) => `channel-rule-flows/${encodeURIComponent(channelId)}`,
  /** GET (BE) — one flow's full detail; used only for a targeted re-fetch, not the initial load. */
  GET: (channelId: string, flowId: string) =>
    `channel-rule-flows/${encodeURIComponent(channelId)}/${encodeURIComponent(flowId)}`,
  /** PUT (BE) — FE-facing persist. Body: { updated_flow, pre_confirm_hash } (from ORCH's
   * serialize response, passed through unmodified). 409 if the flow's stored hash changed since
   * serialize ran (a concurrent re-parse raced this edit) — no write in that case. */
  CONFIRM: (channelId: string, flowId: string) =>
    `channel-rule-flows/${encodeURIComponent(channelId)}/${encodeURIComponent(flowId)}/confirm`,
  /** PATCH (BE) — persists one node's annotation only (PRISM7_CHANNEL_RULES_NODE_DESCRIPTIONS_TDD
   * §3.2). Never touches edges/recipients/labels/text or channel_business_rules, and never calls
   * ORCH — a description edit is unrelated to the serialize/confirm business-rules sequence. */
  UPDATE_NODE_DESCRIPTION: (channelId: string, flowId: string, nodeId: string) =>
    `channel-rule-flows/${encodeURIComponent(channelId)}/${encodeURIComponent(flowId)}/nodes/${encodeURIComponent(nodeId)}/description`,
  /** PATCH (BE) — renames one flow (its display name `flow_name` only; shay-email-backend#564).
   * Never touches nodes/edges/rules text/source hash, and never calls ORCH. BE scopes it to the
   * caller's company and returns the full updated flow. 409 duplicate name in this channel, 404
   * flow gone (e.g. replaced by a re-parse), 422 invalid name. */
  RENAME_FLOW: (channelId: string, flowId: string) =>
    `channel-rule-flows/${encodeURIComponent(channelId)}/${encodeURIComponent(flowId)}/name`,
};

// ORCH's two compute-only channel-rules routes (TDD §5) — never write gg_channel_rule_flows
// themselves (parse persists via BE's internal /replace; serialize never persists at all).
export const CHANNEL_RULES_PARSE_API_ENDPOINTS = {
  /** POST (ORCH) — body {}: reads the channel's current business rules server-side. If a saved
   * flow already matches this exact text (ORCH's reuse gate, PR #627), returns it unchanged with
   * no LLM call; otherwise decomposes into flows via the LLM, persists via BE's internal replace
   * endpoint, and returns the full flow list BE's replace call returned. `force=true` bypasses
   * the reuse gate unconditionally — see channelRuleFlowsService.ts's parseChannelRuleFlows. */
  PARSE: (channelId: string, force?: boolean) =>
    `/shay/email/channel-rules/${encodeURIComponent(channelId)}/parse${force ? '?force=true' : ''}`,
  /** POST (ORCH) — body { edited_node }: pure compute (deterministic substitution when the
   * edited field has an existing text anchor, else a scoped single-flow LLM fallback). Returns
   * { updated_flow, updated_channel_business_rules, used_llm_fallback, pre_confirm_hash }.
   * Never writes anywhere — 409 if BE's flow read shows the row already stale. */
  SERIALIZE: (channelId: string, flowId: string) =>
    `/shay/email/channel-rules/${encodeURIComponent(channelId)}/flows/${encodeURIComponent(flowId)}/serialize`,
};

// Local storage keys
export const SOCKET_KEYS = {
  MEMBER_ADDED_TO_CHANNEL: 1,
  MEMBER_REMOVED_FROM_CHANNEL: 2,
  CHANNEL_CREATED: 3,
  CHANNEL_UPDATED: 4,
  CHANNEL_DELETED: 5,
  CHANNEL_USER_UPDATE: 6,
  CHANNEL_METRICS_UPDATE: 7,
  THREAD_CREATED: 8,
  THREAD_UPDATED: 9,
  THREAD_DELETED: 10,
  THREAD_MESSAGE_CREATED: 11,
  THREAD_MESSAGE_UPDATED: 12,
  THREAD_MESSAGE_DELETED: 13,
  EMAIL_DISCONNECTED: 14,
  EMAIL_RECONNECTED: 15,
  EMBEDDING_STATUS_UPDATED: 16,
  TASK_CREATED: 17,
  TASK_UPDATED: 18,
  TASK_DELETED: 19,
  APPROVAL_CREATED: 20,
  APPROVAL_UPDATED: 21,
  APPROVAL_DELETED: 22,
  DRAFT_GENRATE_STATUS: 23,
};

// Local storage keys
export const STORAGE_KEYS = {
  AUTH_TOKEN: 'authToken',
  REFRESH_TOKEN: 'refreshToken',
  THEME_MODE: 'themeMode',
  USER_ROLE: 'userRole',
  LOGGED_IN_USER_DATA: 'loggedInUserData',
  MY_SUBSCRIPTION_DATA: 'mySubscriptionData',
};

// Pagination defaults
export const PAGINATION = {
  DEFAULT_PAGE_SIZE: 10,
  PAGE_SIZE_OPTIONS: [5, 10, 25, 50],
};

// Email sync constants
export const EMAIL_SYNC = {
  COMPLETION_PERCENTAGE: 10,
  THREAD_COMPLETION_PERCENTAGE: 10,
};

// Email category labels (sidebar + chip display)
export const STANDARD_CATEGORY_LABELS: Record<string, string> = {
  inbox: "Inbox",
  sent: "Sent",
  spam: "Spam",
  no_reply: "No Reply",
  archived: "Archived",
};

/** Copy for the Spam-folder banner (email channels); auto-dismisses after SPAM_FOLDER_NOTICE_COUNTDOWN_SECONDS. */
export const SPAM_FOLDER_NOTICE =
  "If an important email ended up in spam, please move it to your Inbox. We'll learn from your choice and refine our accuracy to make sure it doesn't happen again.";

/** Seconds for the Spam-folder banner countdown (10 → 1, then auto-close). */
export const SPAM_FOLDER_NOTICE_COUNTDOWN_SECONDS = 10;

/** Label before the countdown number (explains the timer dismisses this notice). */
export const SPAM_FOLDER_NOTICE_COUNTDOWN_LABEL = "Auto-closes in";

// Label chip: light background + dark text (Inbox=gray, Sent=blue, No Reply=orange, Spam=red)
export const EMAIL_LABEL_CHIP_BG: Record<string, string> = {
  inbox: "#e0e0e0",
  sent: "#bbdefb",
  no_reply: "#ffe0b2",
  spam: "#ffcdd2",
};
export const EMAIL_LABEL_CHIP_TEXT: Record<string, string> = {
  inbox: "#424242",
  sent: "#1565c0",
  no_reply: "#e65100",
  spam: "#c62828",
};

// Map folder/label display name to category key (include lowercase so API variants match)
export const EMAIL_LABEL_DISPLAY_TO_KEY: Record<string, string> = {
  Inbox: "inbox",
  inbox: "inbox",
  Sent: "sent",
  sent: "sent",
  "No Reply": "no_reply",
  "No reply": "no_reply",
  "no reply": "no_reply",
  no_reply: "no_reply",
  Spam: "spam",
  spam: "spam",
};

// Status colors for chips
export const STATUS_COLORS = {
  ACTIVE: 'success',
  INACTIVE: 'error',
  PENDING: 'warning',
  SUSPENDED: 'error',
  TRIAL: 'warning',
  EXPIRED: 'error',
  ENABLED: 'success',
  DISABLED: 'error',
  OPEN: 'warning',
  CLOSED: 'success',
  IN_PROGRESS: 'info',
} as const;

// User roles
export const USER_ROLES = {
  ADMIN: 'admin',
  USER: 'user',
  SUPPORT: 'support',
} as const;

export type UserRole = typeof USER_ROLES[keyof typeof USER_ROLES];

// Navigation items
export const NAVIGATION_ITEMS = {
  // DASHBOARD: {
  //   path: '/dashboard',
  //   label: 'Dashboard',
  //   icon: 'Dashboard',
  //   roles: [USER_ROLES.ADMIN] as UserRole[],
  // },
  SUPPORT_SPACES: {
    path: '/supportSpace',
    label: LanguageConstants.topic_s,
    icon: 'AllInbox',
    roles: [USER_ROLES.ADMIN, USER_ROLES.USER, USER_ROLES.SUPPORT] as UserRole[],
  },
  TASKS: {
    path: '/tasks',
    label: 'Tasks',
    icon: 'Assignment',
    roles: [USER_ROLES.ADMIN, USER_ROLES.USER, USER_ROLES.SUPPORT] as UserRole[],
  },
  APPROVALS: {
    path: '/approvals',
    label: 'Approvals',
    icon: 'Approval',
    roles: [USER_ROLES.ADMIN, USER_ROLES.USER, USER_ROLES.SUPPORT] as UserRole[],
  },
  ADMIN_HUB: {
    path: '/adminHub',
    label: 'Admin Hub',
    icon: 'AdminPanelSettings',
    roles: [USER_ROLES.ADMIN] as UserRole[],
  },
  PLAN_DETAILS: {
    path: '#plan-details', // Placeholder path for modal items
    label: 'Current Plan Details',
    icon: 'CardMembership',
    roles: [USER_ROLES.ADMIN, USER_ROLES.USER, USER_ROLES.SUPPORT] as UserRole[],
    action: 'modal', // Special flag to indicate this opens a modal
  },
} as const;

// Admin Hub tabs
export const ADMIN_HUB_TABS = {
  USER_MANAGEMENT: {
    value: 'user-management',
    label: 'User Management',
    icon: 'PeopleIcon',
  },
  SUBSCRIPTION_MANAGEMENT: {
    value: 'subscription-management',
    label: 'Subscription Management',
    icon: 'CreditCardIcon',
  },
  DATA_SOURCES: {
    value: 'data-sources',
    label: LanguageConstants.datasource_s,
    icon: 'StorageIcon',
  },
  WORKSPACE_MANAGEMENT: {
    value: 'workspace-management',
    label: 'Workspace Management',
    icon: 'WorkspacesIcon',
  },
  CHAT_SERVICE_MANAGEMENT: {
    value: 'chat-service',
    label: 'Chat Service Management',
    icon: 'ChatIcon',
  },
} as const;

/** Resolve an Admin Hub URL tab value to the index of its currently visible tab. */
export const getAdminHubTabIndex = (
  tab: string | null,
  disableSubscription: boolean,
): number | null => {
  if (!tab) return null;

  const tabIndexes: Record<string, number> = {
    [ADMIN_HUB_TABS.DATA_SOURCES.value]: 0,
    [ADMIN_HUB_TABS.USER_MANAGEMENT.value]: 1,
    [ADMIN_HUB_TABS.WORKSPACE_MANAGEMENT.value]: 2,
  };

  if (disableSubscription) {
    tabIndexes[ADMIN_HUB_TABS.CHAT_SERVICE_MANAGEMENT.value] = 3;
  } else {
    tabIndexes[ADMIN_HUB_TABS.SUBSCRIPTION_MANAGEMENT.value] = 3;
  }

  return tabIndexes[tab] ?? null;
};

export const ADMIN_HUB_ACTIVE_VIEWS = {
  ACCESS_REQUEST: 'access-request',
} as const;

/** Resolve the User Management sub-view from an Admin Hub deep-link. */
export const getAdminHubUserView = (
  tab: string | null,
  activeView: string | null,
): 'onboarded' | 'pending' | 'requests' => {
  if (
    tab === ADMIN_HUB_TABS.USER_MANAGEMENT.value &&
    activeView === ADMIN_HUB_ACTIVE_VIEWS.ACCESS_REQUEST
  ) {
    return 'requests';
  }

  return 'onboarded';
};

/** Query string keys for deep-linking into Admin Hub (e.g. subscription tab + Manage Plan modal) */
export const ADMIN_HUB_URL_PARAMS = {
  TAB: 'tab',
  ACTIVE: 'active',
  /** When '1' or 'true', opens the Manage Plan dialog after navigating to the subscription tab */
  MANAGE_PLAN: 'managePlan',
} as const;

// Theme modes
export const THEME_MODES = {
  SYSTEM: 'system',
  LIGHT: 'light',
  DARK: 'dark',
} as const;

// Environment variables
export const APP_NAME = env.VITE_APP_NAME || 'Prism 7';

// Platform name for payment/subscription APIs (from env VITE_PLATFORM_NAME, e.g. prism7)
export const PLATFORM_NAME = env.VITE_PLATFORM_NAME || 'prism7';
// When true, subscription/payment UI and APIs (mySubscription, fetchCompanySubscriptionDetails) are hidden/disabled
export const DISABLE_SUBSCRIPTION = (env.VITE_DISABLE_SUBSCRIPTION ?? 'false').
toLowerCase() === 'true' || (env.VITE_DISABLE_SUBSCRIPTION ?? '') === '1';

// When true, use file_url for email attachment download instead of presigned_url (set at runtime e.g. Docker)
export const DOWNLOAD_FILE = (env.VITE_DOWNLOAD_FILE ?? 'false').
toLowerCase() === 'true' || (env.VITE_DOWNLOAD_FILE ?? '') === '1';

// Visibility switch for the legacy Channel Settings "Channel Workflow" tab
// (VITE_SHOW_WORKFLOW_TAB). Business Rules Workflow is always available to channel admins.
// true/'1' (default) shows Channel Workflow; false/'0' hides it — same on/off convention as
// DISABLE_SUBSCRIPTION.
export const SHOW_WORKFLOW_TAB = (env.VITE_SHOW_WORKFLOW_TAB ?? 'true').
toLowerCase() !== 'false' && (env.VITE_SHOW_WORKFLOW_TAB ?? '') !== '0';

// When true, subscription/payment UI and APIs are hidden/disabled; limit modals show "contact admin" instead of "Upgrade your plan"
// export const DISABLE_SUBSCRIPTION = true;

// Messages shown in modal when subscription is disabled (VITE_DISABLE_SUBSCRIPTION true) — user should contact admin
export const SUBSCRIPTION_LIMIT_MESSAGES = {
  CHANNEL: 'You have reached your channel creation limit. Please contact support team.',
  MESSAGE: 'You have reached your message limit. Please contact support team.',
  DATA_STORAGE: 'You have reached your data storage limit. Please contact support team.',
} as const;

// Marketplace image type: 'gmail' | 'outlook' | '' | undefined. When set, only show features for that provider.
export const MARKETPLACE_IMAGE_TYPE = (env.VITE_MARKETPLACE_IMAGE_TYPE ?? '').toLowerCase();
// Show Gmail connection and Google apps (Drive, Calendar) when type is not 'outlook'
export const SHOW_GMAIL_FEATURES = MARKETPLACE_IMAGE_TYPE !== 'outlook';
// Show Outlook connection and Outlook/SharePoint apps when type is not 'gmail'
export const SHOW_OUTLOOK_FEATURES = MARKETPLACE_IMAGE_TYPE !== 'gmail';
// App keys to hide when image type is 'gmail' (Outlook-related apps)
const OUTLOOK_APP_KEYS = ['microsoft_outlook_calendar', 'sharepoint'];
// App keys to hide when image type is 'outlook' (Gmail-related apps)
const GMAIL_APP_KEYS = ['google_drive', 'google_calendar'];

/** App keys for cloud-drive integrations on the Drives tab (API app_key / datasource app_type). */
export const DRIVE_APP_KEYS = ['google_drive', 'dropbox', 'sharepoint'] as const;

/** Normalise app_key / app_type from API (casing may vary). */
export function normalizeAppKey(appKey: string): string {
  return appKey.trim().toLowerCase();
}

export function isDriveAppKey(appKey: string): boolean {
  return (DRIVE_APP_KEYS as readonly string[]).includes(normalizeAppKey(appKey));
}

/**
 * Comma-separated app keys hidden from marketplace UI across all deployments.
 * Evaluated at call time so runtime config.js values are respected.
 * Override via VITE_HIDDEN_APP_KEYS (e.g. '' to show all, 'sharepoint' to hide SharePoint).
 */
export function getHiddenAppKeys(): readonly string[] {
  return (getRuntimeEnv().VITE_HIDDEN_APP_KEYS ?? '')
    .split(',')
    .map((key) => normalizeAppKey(key))
    .filter(Boolean);
}

export function isAppKeyHidden(appKey: string): boolean {
  return getHiddenAppKeys().includes(normalizeAppKey(appKey));
}

/** Filter app keys by marketplace image type only. Returns true if the app should be shown. */
function matchesMarketplaceImageType(appKey: string): boolean {
  if (!appKey) return true;
  const key = normalizeAppKey(appKey);
  if (MARKETPLACE_IMAGE_TYPE === 'gmail') return !OUTLOOK_APP_KEYS.includes(key);
  if (MARKETPLACE_IMAGE_TYPE === 'outlook') return !GMAIL_APP_KEYS.includes(key);
  return true;
}

/** Marketplace visibility: image-type rules plus env-driven hidden app keys. */
export function shouldShowAppInMarketplace(appKey: string): boolean {
  if (!appKey) return true;
  if (isAppKeyHidden(appKey)) return false;
  return matchesMarketplaceImageType(appKey);
}

/**
 * @deprecated Use shouldShowAppInMarketplace instead.
 * Delegates to the full marketplace visibility check (image type + hidden app keys).
 */
export function shouldShowAppByMarketplaceImageType(appKey: string): boolean {
  return shouldShowAppInMarketplace(appKey);
} 

// Google OAuth Configuration
export const GMAIL_CLIENT_ID = env.VITE_GOOGLE_CLIENT_ID || "203457627308-8f4j8mh1hoi4gpaj4964l6pj4b8fnhit.apps.googleusercontent.com";

// Google Chat Inbox OAuth client — falls back to the Gmail client ID until a dedicated one is
// provisioned (pending decision, see docs/GoogleChatInbox_DM_FE_Implementation.md §8 item 2).
export const GCHAT_INBOX_CLIENT_ID = env.VITE_GCHAT_INBOX_CLIENT_ID || GMAIL_CLIENT_ID;

// Google Drive OAuth Configuration
export const GOOGLE_DRIVE_CLIENT_ID = env.VITE_GOOGLE_DRIVE_CLIENT_ID || "203457627308-afaghhpsitmqdvm869n4e735bkn7a684.apps.googleusercontent.com";

// Google Calendar OAuth Configuration
export const GOOGLE_CALENDAR_CLIENT_ID = env.VITE_GOOGLE_CALENDAR_CLIENT_ID || "203457627308-pab401lnpa1ru2rfkdd8b9enesbb3mb1.apps.googleusercontent.com";
// Note: GOOGLE_CALENDAR_CLIENT_SECRET removed - get from server token exchange response

// Outlook Calendar OAuth Configuration
export const OUTLOOK_CALENDAR_CLIENT_ID = env.VITE_OUTLOOK_CALENDAR_CLIENT_ID || "c15226fd-56b4-4bf1-ba8c-573dc2df578c";
// Note: OUTLOOK_CALENDAR_CLIENT_SECRET removed - get from server token exchange response

// SharePoint OAuth Configuration
export const SHAREPOINT_CLIENT_ID = env.VITE_SHAREPOINT_CLIENT_ID || "c15226fd-56b4-4bf1-ba8c-573dc2df578c";
// Note: SHAREPOINT_CLIENT_SECRET removed - get from server token exchange response

// Jira OAuth Configuration
export const JIRA_CLIENT_ID = env.VITE_JIRA_CLIENT_ID || "9lm6IyDF8uxsYxVHvnJnM5glxONTV4sn";

// Office 365 OAuth Configuration
export const OFFICE365_CLIENT_ID = env.VITE_OFFICE365_CLIENT_ID || "c15226fd-56b4-4bf1-ba8c-573dc2df578c";

// New OAuth Providers Configuration
export const SERVICENOW_CLIENT_ID = env.VITE_SERVICENOW_CLIENT_ID || "";
export const SALESFORCE_CLIENT_ID = env.VITE_SALESFORCE_CLIENT_ID || "";
export const ZOHO_CRM_CLIENT_ID = env.VITE_ZOHO_CRM_CLIENT_ID || "";
export const ZENDESK_CLIENT_ID = env.VITE_ZENDESK_CLIENT_ID || "";

// Dynamic form configuration for Assign Task and Approval panels
export interface FormField {
  id: string;
  type: 'text' | 'select' | 'textarea' | 'dynamic-list' | 'file-upload';
  label: string;
  placeholder?: string;
  required: boolean;
  fullWidth?: boolean;
  gridSize: number;
  multiline?: boolean;
  rows?: number;
  startAdornment?: 'calendar' | 'clock';
  validation?: {
    minLength?: number;
    maxLength?: number;
    pattern?: string;
  };
  options?: Array<{ value: string; label: string }>;
  apiDependent?: boolean;
  apiField?: string;
  config?: {
    placeholder?: string;
    addButtonText?: string;
    minItems?: number;
    maxItems?: number;
  };
}

export interface FormConfig {
  fields: FormField[];
  layout?: {
    spacing?: number;
  };
  validation?: {
    required?: string[];
  };
  api?: {
    endpoint?: string;
    method?: string;
  };
}

// Dynamic form configuration for Assign Task Panel
export const taskFormConfig: FormConfig = {
  fields: [
    {
      id: 'taskName',
      type: 'text',
      label: 'Task Name',
      placeholder: 'Task name..',
      required: true,
      fullWidth: true,
      gridSize: 12,
      validation: {
        minLength: 1,
        maxLength: 100
      }
    },
    {
      id: 'priority',
      type: 'select',
      label: 'Priority',
      required: true,
      options: [
        { value: 'Low', label: 'Low' },
        { value: 'Medium', label: 'Medium' },
        { value: 'High', label: 'High' },
        { value: 'Critical', label: 'Critical' },
        { value: 'Blocker', label: 'Blocker' }
      ],
      gridSize: 6
    },
    {
      id: 'assignTo',
      type: 'select',
      label: 'Assign to',
      required: true,
      options: [], // Will be populated from API
      gridSize: 6,
      apiDependent: true,
      apiField: 'members'
    },
    {
      id: 'dueDate',
      type: 'text',
      label: 'Date',
      placeholder: '08-22-2025',
      required: false,
      gridSize: 6,
      startAdornment: 'calendar'
    },
    {
      id: 'dueTime',
      type: 'select',
      label: 'Time',
      placeholder: 'Time',
      required: false,
      gridSize: 6,
      options: [] // Will be populated dynamically
    },
    {
      id: 'checklist',
      type: 'dynamic-list',
      label: 'Checklist',
      required: false,
      fullWidth: true,
      gridSize: 12,
      config: {
        placeholder: 'Checklist item',
        addButtonText: 'Checklist item',
        minItems: 0,
        maxItems: 10
      }
    },
    {
      id: 'comment',
      type: 'textarea',
      label: 'Add Comment',
      placeholder: 'Add comment...',
      required: false,
      fullWidth: true,
      gridSize: 12,
      multiline: true,
      rows: 4
    },
    {
      id: 'attachments',
      type: 'file-upload',
      label: 'Attachments',
      required: false,
      fullWidth: true,
      gridSize: 12
    }
  ],
  layout: {
    spacing: 1
  },
  validation: {
    required: ['taskName', 'priority', 'assignTo']
  },
  api: {
    endpoint: '/api/tasks',
    method: 'POST'
  }
};

// Dynamic form configuration for Approval Panel
export const approvalFormConfig: FormConfig = {
  fields: [
    {
      id: 'approver',
      type: 'select',
      label: 'Approver',
      required: true,
      options: [], // Will be populated from API
      gridSize: 12,
      apiDependent: true,
      apiField: 'members'
    }
  ],
  layout: {
    spacing: 2
  },
  validation: {
    required: ['approver']
  },
  api: {
    endpoint: '/api/approvals',
    method: 'POST'
  }
};

// Channel Types Configuration
export interface ChannelTypeConfig {
  id: string;
  name: string;
  label: string;
  description: string;
  icon: string; // Icon name from Material-UI icons
  color: string;
}

export const CHANNEL_TYPES_CONFIG: ChannelTypeConfig[] = [
  {
    id: 'email',
    name: 'email',
    label: 'Email',
    description: 'Collaborate on support emails with your team in one place.',
    icon: 'Email',
    color: '#1976d2'
  },
  {
    id: 'message',
    name: 'message',
    label: 'Message',
    description: 'Get instant answers from your connected knowledge base as messages.',
    icon: 'Chat',
    color: '#4caf50'
  },
  {
    id: 'voice',
    name: 'voice',
    label: 'Voice',
    description: 'Manage customer calls with integrated voice support and workflows.',
    icon: 'Mic',
    color: '#ff9800'
  }
];

// Helper function to get channel type by id
export const getChannelTypeById = (id: string): ChannelTypeConfig | undefined => {
  return CHANNEL_TYPES_CONFIG.find(type => type.id === id);
};

// Helper function to get channel type by name
export const getChannelTypeByName = (name: string): ChannelTypeConfig | undefined => {
  return CHANNEL_TYPES_CONFIG.find(type => type.name === name);
};
